import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import {
  useMigrationSearchMutation,
  useDeleteMigrationMutation,
  useFireMigrationMutation,
} from './hooks/useMigrationQuery'

/**
 * Port of `app/migration/migration.html` + `app/migration/
 * migrationController.js` (AngularJS `migrationController`) — the
 * "Migrações" listing screen, reached at `/workspace/migration` (legacy
 * state `workspace.migration`, see `AppRoutes.jsx`).
 *
 * Legacy behavior ported 1:1 (backed by `useMigrationQuery.js`'s hooks):
 * - A single "namespace" text filter + "Buscar" button
 *   (`checkStatus(namespace)`): appends a new row for that namespace to the
 *   list (never replaces the whole list — matches the legacy service's
 *   `migrations = migrations.concat(resp.data)`).
 * - Per-row "Atualizar" button (`checkStatus(migration.namespace, $index)`):
 *   re-fetches just that row and replaces it in place (`migrations[index] =
 *   resp.data`), ported here via each row's own index.
 * - Per-row "Visualizar" button (`viewDetails(namespace)`): navigates to
 *   `workspace.migration-details` (`/workspace/migration/:namespace` here) —
 *   that screen itself is task 022's scope, this page only owns the
 *   navigation trigger, matching `$state.go('workspace.migration-details',
 *   {namespace})`.
 * - Per-row "Deletar" button (`deleteMigration(namespace)`): legacy uses a
 *   native `confirm(...)` dialog before calling
 *   `MigrationService.deleteMigration`. No other ported module has an
 *   identical native-`confirm()` precedent to reuse; the closest is
 *   `BannerPage.jsx`'s upload/remove MUI `Dialog` confirmations (task 014),
 *   which this follows instead of introducing `window.confirm()` — a plain
 *   browser dialog would be inconsistent with the MUI-based UI kit decision
 *   in `plan.md` ("UI Kit" section) and with the rest of the app. Documented
 *   here as the explicit decision for this otherwise-unspecified mapping.
 * - Per-row "Disparar" button (`fireMigration(migration)`), disabled unless
 *   `migration.status === 'READY'` (`ng-disabled="migration.status !=
 *   'READY'"`): sets the row's status to `'RUNNING'` optimistically before
 *   calling `MigrationService.fireMigration`, exactly like the legacy
 *   controller (`migration.status = 'RUNNING'` is set synchronously before
 *   the `$http` call, not after a response).
 * - "Mostrando: N" label shown only while there is at least one row
 *   (`ng-show="service.migrations.length"`).
 * - Loading indicator (`#fountainG`/Materialize indeterminate `.progress`,
 *   toggled by `$scope.loading`) -> MUI `LinearProgress`, shown while the
 *   search mutation is pending (row-level refresh/delete/fire actions do not
 *   toggle this page-level bar in the legacy code either — `loading` is only
 *   ever set inside `checkStatus`, which both the main search and the
 *   per-row refresh button call, so it *is* shared across both; reproduced
 *   here by reusing the same `useMigrationSearchMutation` instance and its
 *   `isPending` for both the main search and the row-level refresh action).
 * - `AuthService.getUser()` guard before every action, only used in the
 *   legacy controller to read `urlAstreaPath`/show a "could not load user"
 *   toast on failure — the equivalent session/env data is already available
 *   unconditionally here via `useAuth()` (task 009) once a user reaches this
 *   route at all (`ProtectedRoute`, task 011), so that extra round trip
 *   is not reproduced; its only user-visible effect (an error toast when the
 *   session can't be resolved) can't actually happen once already inside
 *   `ProtectedRoute`.
 *
 * Deliberately not ported: the `#fountainG`/`.wrap-responsive`/`inline-flex`
 * Materialize layout chrome and the `$(document).ready(...)` jQuery tooltip
 * initialization — purely presentational Materialize/jQuery wiring with no
 * behavioral meaning, superseded by MUI's own primitives (icons carry
 * `aria-label`s instead of Materialize tooltips) per `plan.md`'s UI-kit
 * decision.
 *
 * MUI `Table` (not `DataGrid`) is used here, matching every other listing
 * screen already ported (`HistoriesPage`, `ClippingsListPage`,
 * `ActionAuditPage`): the legacy screen has no real pagination/sorting/
 * filtering to justify `DataGrid` (`page` is dead state in the sibling
 * `clippings-list` module, and this screen has no pagination markup at all),
 * and introducing a new dependency (`@mui/x-data-grid`, not in
 * `client/package.json`) for a handful of rows this app manages via a single
 * shared array would be inconsistent with the rest of the module set.
 */

function MigrationListPage() {
  const navigate = useNavigate()
  const searchMutation = useMigrationSearchMutation()
  const deleteMutation = useDeleteMigrationMutation()
  const fireMutation = useFireMigrationMutation()

  const [namespace, setNamespace] = useState('')
  const [migrations, setMigrations] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })
  const [confirmDelete, setConfirmDelete] = useState(null)

  function notifyError(error) {
    const message = error?.response?.data?.errorMessage || 'Erro desconhecido'
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  /** Port of `checkStatus(namespace, index)`. */
  function checkStatus(targetNamespace, index) {
    if (!targetNamespace) return

    searchMutation.mutate(
      { namespace: targetNamespace },
      {
        onSuccess: (data) => {
          setMigrations((current) => {
            if (index != null) {
              return current.map((migration, i) => (i === index ? data : migration))
            }
            return [...current, data]
          })
        },
        onError: (error) => notifyError(error),
      },
    )
  }

  function handleSearch() {
    checkStatus(namespace)
  }

  function handleRefresh(targetNamespace, index) {
    checkStatus(targetNamespace, index)
  }

  /** Port of `viewDetails(namespace)`: `$state.go('workspace.migration-details', {namespace})`. */
  function handleViewDetails(targetNamespace) {
    navigate(`/workspace/migration/${targetNamespace}`)
  }

  /** Port of `deleteMigration(namespace)`'s `confirm(...)` gate — see doc-comment above. */
  function handleDeleteClick(targetNamespace) {
    setConfirmDelete(targetNamespace)
  }

  function handleCancelDelete() {
    setConfirmDelete(null)
  }

  function handleConfirmDelete() {
    const targetNamespace = confirmDelete
    setConfirmDelete(null)

    deleteMutation.mutate(targetNamespace, {
      onSuccess: () => setSnackbar({ open: true, message: 'Success!' }),
      onError: (error) => notifyError(error),
    })
  }

  /** Port of `fireMigration(migration)`. */
  function handleFireMigration(migration, index) {
    if (migration.status !== 'READY') return

    setMigrations((current) =>
      current.map((item, i) => (i === index ? { ...item, status: 'RUNNING' } : item)),
    )

    fireMutation.mutate(migration.namespace, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: 'Migração disparada! Clique em buscar novamente para acompanhar a migração.',
        })
      },
      onError: (error) => {
        setSnackbar({ open: true, message: `ERRO: ${error?.message || 'Erro desconhecido'}` })
      },
    })
  }

  const isLoading = searchMutation.isPending

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Migrações
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            id="namespace"
            label="Digite um namespace"
            value={namespace}
            onChange={(event) => setNamespace(event.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={handleSearch} disabled={isLoading} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            Buscar
          </Button>
        </Stack>
      </Paper>

      {migrations.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Mostrando: {migrations.length}
        </Typography>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                Namespace
                <br />
                Origem
                <br />
                JobId
              </TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Inválidos</TableCell>
              <TableCell>Na fila</TableCell>
              <TableCell>Migrando</TableCell>
              <TableCell>Migrados</TableCell>
              <TableCell>Erros</TableCell>
              <TableCell>No cliente</TableCell>
              <TableCell>Status</TableCell>
              <TableCell colSpan={4} />
            </TableRow>
          </TableHead>
          <TableBody>
            {migrations.map((migration, index) => (
              <TableRow key={`${migration.namespace}-${index}`}>
                <TableCell>
                  {migration.namespace}
                  <br />
                  {migration.origin}
                  <br />
                  {migration.jobId}
                </TableCell>
                <TableCell>{migration.total}</TableCell>
                <TableCell>{migration.invalid}</TableCell>
                <TableCell>{migration.ready}</TableCell>
                <TableCell>{migration.inProgress}</TableCell>
                <TableCell>{migration.done}</TableCell>
                <TableCell>{migration.error}</TableCell>
                <TableCell>{migration.inNamespace}</TableCell>
                <TableCell>{migration.status}</TableCell>
                <TableCell>
                  <IconButton
                    color="success"
                    aria-label="Atualizar"
                    onClick={() => handleRefresh(migration.namespace, index)}
                  >
                    <RefreshIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    aria-label="Visualizar"
                    onClick={() => handleViewDetails(migration.namespace)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="error"
                    aria-label="Deletar"
                    onClick={() => handleDeleteClick(migration.namespace)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="success"
                    aria-label="Disparar"
                    disabled={migration.status !== 'READY'}
                    onClick={() => handleFireMigration(migration, index)}
                  >
                    <SendIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmDelete !== null} onClose={handleCancelDelete}>
        <DialogTitle>Deletar migração</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deletar JSONs de migração do namespace &apos;{confirmDelete}&apos;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MigrationListPage
